import {
    Component,
    forwardRef,
    inject,
    Input,
    OnInit,
    Output,
    ChangeDetectionStrategy
} from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import {
    MapDialogComponent,
    MapResult
} from '../map-dialog/map-dialog.component';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-plant-location-component',
    standalone: true,
    imports: [
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatDialogModule,
        TranslatePipe
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PlantLocationComponent),
            multi: true
        }
    ],
    templateUrl: './plant-location-component.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './plant-location-component.component.scss'
})
export class PlantLocationComponent {
    private dialog = inject(MatDialog);

    // value held by the control (MapResult or null)
    private value: MapResult | null = null;
    display = '';

    // form control disabled state (comes from setDisabledState)
    disabled = false;

    // explicit editable input from parent: if false modal won't open
    @Input() editable: boolean = true;

    // ControlValueAccessor callbacks
    private onChange: (v: any) => void = () => {};
    private onTouched: () => void = () => {};

    // --- ControlValueAccessor API ---
    writeValue(val: MapResult | null): void {
        this.value = val ?? null;
        this.display = this.value
            ? `${this.value.lat.toFixed(6)}, ${this.value.lng.toFixed(6)}`
            : '';
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    // Angular calls this when formControl.disable()/enable() is invoked
    setDisabledState(isDisabled: boolean): void {
        this.disabled = !!isDisabled;
    }

    // --- public API to open dialog ---
    openDialog(): void {
        // Respect both the form-control disabled state and the explicit editable input
        if (this.disabled) {
            console.debug(
                '[PlantLocation] openDialog blocked: control is disabled'
            );
            return;
        }
        if (!this.editable) {
            console.debug('[PlantLocation] openDialog blocked: editable=false');
            return;
        }

        const ref = this.dialog.open(MapDialogComponent, {
            width: '720px',
            data: { initial: this.value ?? undefined },
            autoFocus: false
            // restoreFocus: true // optional
        });

        ref.afterClosed().subscribe((res: MapResult | undefined) => {
            if (!res) {
                // user cancelled: mark touched and return
                this.onTouched();
                return;
            }
            // set new value and notify form
            this.value = res;
            this.display = `${res.lat.toFixed(6)}, ${res.lng.toFixed(6)}`;
            this.onChange(res);
            this.onTouched();
        });
    }
}
